import Clock from "@geist-ui/react-icons/clock";
import UserIcon from "@geist-ui/react-icons/user";
import { Lesson, User } from "@prisma/client";
import dayjs from "dayjs";
import MapPin from "@geist-ui/react-icons/mapPin";
import CreditCard from "@geist-ui/react-icons/creditCard";
import useModal from "@xtribe/shared/components/lib/Modal/use-modal";
import Modal from "@xtribe/shared/components/lib/Modal";
import Button from "@xtribe/shared/components/lib/Button";
import getSymbolFromCurrency from "currency-symbol-map";
import { useUser } from "@xtribe/shared/hooks/useUser";
import { useTeam } from "@xtribe/shared/hooks/useTeam";
import { useState } from "react";
import { useRouter } from "next/router";
import { postFetcher } from "@xtribe/shared/lib/defaultFetcher";
import { addColorAlpha, getCorrectTextColor } from "@xtribe/shared/utils/color";
import { useRedirect } from "@xtribe/shared/hooks/useRedirect";
import { EventContentData } from "../../../hooks/useEvents/types";
import { createBucketLink } from "../../../../shared/lib/createBucketLink";

type Props = {
  content: EventContentData;
  isWidget?: boolean;
};

const CalendarLesson: React.FC<Props> = ({ content, isWidget }) => {
  const { visible, setVisible, bindings } = useModal();
  const { user } = useUser();
  const { team } = useTeam();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const { createNextPathUrl } = useRedirect({ defaultPath: "/member" });
  const [errorMessage, setErrorMessage] = useState("");
  if (content.event?.startTime && content.event?.endTime) {
    const orderLesson = async (id: string) => {
      const signupWithLessonPathLink = createNextPathUrl(
        `/signin`,
        `/member/c/events/${id}`
      );
      if (isWidget) {
        window.open(
          user ? `/member/c/events/${id}` : signupWithLessonPathLink,
          "_blank"
        );
      } else {
        setVisible(false);
        setTimeout(() => router.push(`/member/c/events/${id}`), 300);
      }
    };
    let timeline: "today" | "after" | "before";
    const now = dayjs(new Date());
    const tomorrow = dayjs(now)
      .add(1, "day")
      .hour(0)
      .minute(0)
      .second(0)
      .millisecond(0);
    if (dayjs(content.event?.endTime * 1000).isBefore(now)) {
      timeline = "before";
    } else if (
      dayjs(content.event?.endTime * 1000).isAfter(now) &&
      dayjs(content.event?.startTime * 1000).isBefore(tomorrow)
    ) {
      timeline = "today";
    } else {
      timeline = "after";
    }

    const color = content.color;
    const textColor = getCorrectTextColor(content.color);
    return (
      <>
        <div
          role="button"
          onClick={() => setVisible(timeline !== "before")}
          className={`lesson-container px-2 pt-4 pb-4 border ${
            timeline === "before" ? "border-gray-100" : ""
          } rounded-lg ${
            timeline !== "before"
              ? "cursor-pointer hover:shadow-sm"
              : "cursor-default"
          }`}
        >
          <p className="text-sm font-medium">{content.title}</p>
          <div className="flex items-center mt-2">
            <Clock className="opacity-75" size={16} />
            <p className="text-xs opacity-75 ml-2">
              {dayjs(content.event?.startTime * 1000).format("HH:mm ")}-
              {dayjs(content.event?.endTime * 1000).format(" HH:mm")}
            </p>
          </div>
          <div className="flex items-center mt-2">
            <UserIcon className="opacity-75" size={16} />
            <p className="text-xs opacity-75 ml-2">
              {content.event?.instructor}
            </p>
          </div>
          <div className="flex items-center mt-2">
            <MapPin className="opacity-75" size={16} />
            <p className="text-xs opacity-75 ml-2">{content.event?.location}</p>
          </div>
          {/* {content.price > 0 && (
            <div className="flex items-center mt-2">
              <CreditCard className="opacity-75" size={16} />
              <p className="text-xs opacity-75 ml-2">
                {content.price}${getSymbolFromCurrency(content.currency ?? "EUR")}
              </p>
            </div>
          )} */}
        </div>
        {timeline !== "before" && (
          <Modal {...bindings} width="32rem">
            <div className="text-left">
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                <img
                  src={createBucketLink(
                    content.image || "no-image-selected.jpg"
                  )}
                  className="z-10 h-full w-full absolute left-0 top-0 object-cover"
                />
              </div>
              <h3 className="text-xl mt-2 font-medium sm:text-xl">
                {content.title}
              </h3>
              <div className="flex items-center mt-2">
                <Clock className="opacity-75" size={16} />
                <p className="text-xs opacity-75 ml-2">
                  {dayjs(content.event?.startTime * 1000).format(
                    "DD. MMM HH:mm "
                  )}
                  -{dayjs(content.event?.endTime * 1000).format(" HH:mm")}
                </p>
              </div>
              <div className="flex items-center mt-2">
                <UserIcon className="opacity-75" size={16} />
                <p className="text-xs opacity-75 ml-2">
                  {content.event?.instructor}
                </p>
              </div>
              <div className="flex items-center mt-2">
                <MapPin className="opacity-75" size={16} />
                <p className="text-xs opacity-75 ml-2">
                  {content.event?.location}
                </p>
              </div>
              <div className="flex items-center mt-2">
                <CreditCard className="opacity-75" size={16} />
                <p className="text-xs opacity-75 ml-2">
                  {content.price > 0
                    ? `${content.price}${getSymbolFromCurrency(
                        content.currency ?? "EUR"
                      )}`
                    : "Kostenlos"}
                </p>
              </div>
              <div className="flex flex-col mt-4">
                <Button
                  type="success"
                  onClick={() => orderLesson(content.id)}
                  loading={isLoading}
                >
                  Teilnehmen
                </Button>
              </div>
              <p className="mt-4 text-sm">{content.description}</p>
              <p className="mt-8 text-lg font-semibold mb-2">
                Online-Übertragung{" "}
              </p>
              <p className="mb-4 text-sm">
                Für diese Aktivität gibt es eine Online-Übertragung. Um
                teilzunehmen, beachte folgende Schritte:
              </p>
              <ul className="pl-8 list-outside list-disc">
                <li className="mb-4 text-sm">
                  Wir nutzen zur Online-Übertragung einen Streaming-Anbieter.
                  Lade dir ggf. die dazu nötige Software oder App auf deinen
                  Laptop, Tablet oder Smartphone herunter.
                </li>
                <li className="mb-4 text-sm">
                  15 Minuten vor Beginn der Aktivität ist der Zugang zur
                  Online-Übertragung verfügbar.
                </li>
                <li className="mb-4 text-sm">
                  Bei bestimmten Streaming-Anbietern (z.B. Zoom) ist es möglich,
                  dass Teilnehmer über ihre Kamera von anderen Teilnehmern
                  gesehen werden können. Wenn du dich selbst oder das
                  persönliche Umfeld/die Umgebung, in der du an der
                  Online-Klasse teilnimmst, nicht zeigen möchtest, kannst du die
                  Kamerafunktion deaktivieren oder die Kamera abdecken. Der
                  Name, den du beim Betreten des Online-Meeting-Raums angibst,
                  wird anderen Teilnehmern angezeigt. Du kannst deinen Namen
                  aber jederzeit gerne anpassen (z.B. nur die Initialen
                  angeben).
                </li>
              </ul>
            </div>
          </Modal>
        )}
        <style jsx>{`
          .lesson-container {
            background-color: ${timeline !== "before" ? color : "#e2e2e2"};
            color: ${timeline !== "before" ? textColor : "#000"};
          }
        `}</style>
      </>
    );
  }
  return null;
};

export default CalendarLesson;
